/* ============================================================
   DASHBOARD — SESAAT Studio v3.9
   ============================================================
   Fail ini berkhidmat kepada DUA laluan, dikawal oleh `modSebenar`
   (corak yang sama seperti ExtensionV39View / MarketplaceV39View —
   jangan cipta corak baharu):

     modSebenar = false  →  /dashboard-v3.9  (PELAN reka bentuk)
     modSebenar = true   →  /dashboard       (HALAMAN HIDUP, suis v3.9 hidup)

   Rupa lama kekal dalam GettingStartedView.jsx dan TIDAK dipadam —
   itu yang menjadikan patah balik percuma (tutup suis, tiada deploy).

   ⛔ TIADA logic Firestore ditulis dalam fail ini. Kedua-dua rupa
      berkongsi satu otak: src/hooks/useDashboard.js.

   ── REVAMP DIBUAT SATU SEKSYEN DEMI SATU ──────────────────────
   Setakat ini DUA seksyen sahaja yang sudah bertukar rupa:
     ✅ Ucapan + hasrat harian
     ✅ Group sokongan WhatsApp
   Yang lain (notis kata laluan, Produk Baru, Kalendar Kempen,
   footer) SENGAJA dikekalkan bentuk, susunan dan fungsi asalnya —
   ia datang daripada DashboardSections.jsx, fail yang SAMA yang
   dipakai rupa lama, jadi dua rupa itu mustahil terpesong.

   ⚠️ Seksyen lama itu dibalut satu bekas berkelas `dark` supaya
      ia memakai palet gelapnya sendiri. Tanpa itu kotak putihnya
      menyilaukan dalam panel gelap v3.9. Bila seksyen berkenaan
      direvamp nanti, import + bekas `dark` itu tanggal sekali.

   Seksyen di bawah mengikut turutan yang SAMA dengan halaman
   asal supaya perbandingan adil:
      1. Notis tetapkan kata laluan (akaun Google sahaja)
      2. Ucapan + hasrat boleh edit  ·  Group sokongan WhatsApp
      3. Produk Baru (10 kad, "Lihat semua" → System library)
      4. Kalendar Kempen
      5. Footer

   ⚠️ Keadaan susah turut disertakan dalam MOD PELAN supaya pelan
      ini lengkap, bukan cantik masa senang sahaja — lihat KEADAAN.
   ============================================================ */

import { useState } from 'react';
import {
    KeyRound, Pencil, MessageCircle, ArrowRight, CalendarDays, Zap, Check, X,
} from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, PageHeader, Card, CardBody, Segmented, StatTile, EmptyState } from '../design/ui';

/* useLang (bukan useT sahaja) — kalendar kempen perlu tahu bahasa
   AKTIF, bukan hanya cara menterjemah: nama bulan dan hari datang
   daripada senarai kalendar, bukan daripada kamus. */
import { useLang } from '../i18n';
import { useDashboard, bolehLihatProdukBaru } from '../hooks/useDashboard';
import CampaignCalendarSection from './CampaignCalendarSection';
/* Footer TIDAK diimport dengan sengaja — rel menu v3.9 sudah memapar
   "© 2026 Sesaat · v3.x" di kakinya, jadi footer halaman ialah salinan
   kedua bagi maklumat yang sama. Rupa lama masih memakainya (rel lama
   tiada baris hak cipta), jadi ia kekal dalam DashboardSections.jsx. */
import { PasswordSetupNotice, NewProductsSection } from './DashboardSections';

/* Keadaan yang boleh diuji pada pelan ini. Halaman sebenar akan
   memilihnya berdasarkan data; di sini ia suis supaya kau boleh
   nilai setiap rupa tanpa perlu mengarang data.

   `value` = nilai data (dibandingkan dalam logik di bawah) — JANGAN
   terjemah. `labelKey` menyimpan KUNCI kamus, bukan ayat: pemalar
   aras-modul tidak boleh memanggil useT(), jadi terjemahan berlaku
   pada masa render. */
const KEADAAN = [
    { value: 'biasa', labelKey: 'dashboard.state.normal' },
    { value: 'baru', labelKey: 'dashboard.state.newUser' },
    { value: 'kosong', labelKey: 'dashboard.state.noProducts' },
];

const PRODUK_BARU = [
    { id: 1, nama: 'Serum Vitamin C Glow Booster 30ml', warna: 'linear-gradient(135deg,#f9a8d4,#be185d)', ready: true },
    { id: 2, nama: 'Set Tudung Bawal Premium Cotton Voal', warna: 'linear-gradient(135deg,#fcd34d,#b45309)', ready: true },
    { id: 3, nama: 'Minyak Wangi Attar Musk Al-Haramain', warna: 'linear-gradient(135deg,#a5b4fc,#4338ca)', ready: false },
    { id: 4, nama: 'Umpan Semut Ant Bait Roach Killer', warna: 'linear-gradient(135deg,#86efac,#15803d)', ready: true },
    { id: 5, nama: 'DRY Pro Baju Hujan Motorcycle Rainsuit', warna: 'linear-gradient(135deg,#fca5a5,#b91c1c)', ready: true },
    { id: 6, nama: 'Official Glow Skin Toner Hydrating', warna: 'linear-gradient(135deg,#67e8f9,#0e7490)', ready: true },
    { id: 7, nama: 'Beg Galas Kalis Air Nylon 20L', warna: 'linear-gradient(135deg,#d8b4fe,#7e22ce)', ready: false },
    { id: 8, nama: 'Lampu Meja LED Boleh Laras Cahaya', warna: 'linear-gradient(135deg,#fdba74,#c2410c)', ready: true },
    { id: 9, nama: 'Botol Air Termos Keluli 750ml', warna: 'linear-gradient(135deg,#93c5fd,#1d4ed8)', ready: true },
    { id: 10, nama: 'Cermin Solek Meja Dengan Lampu', warna: 'linear-gradient(135deg,#f0abfc,#a21caf)', ready: true },
];

/* Kempen — halaman sebenar menjananya daripada formula tarikh
   (src/data/campaignCalendar.js), jadi seksyen ini tidak pernah
   kosong walaupun Firestore gagal dibaca. Sifat itu dikekalkan
   dalam pelan ini: ia sentiasa ada isi. */
const KEMPEN = [
    { tarikh: '8.8', nama: 'Kempen Mega 8.8', hari: 1, jenis: 'platform' },
    { tarikh: '15 Ogos', nama: 'Payday Sale', hari: 8, jenis: 'platform' },
    { tarikh: '31 Ogos', nama: 'Hari Kebangsaan', hari: 24, jenis: 'cuti' },
    { tarikh: '9.9', nama: 'Kempen Mega 9.9', hari: 33, jenis: 'platform' },
];

export default function DashboardV39View({ modSebenar = false, onNavigate, user = null, isPaid = false }) {
    const { t, lang } = useLang();

    /* State mod PELAN sahaja — data contoh, tiada kesan pada akaun sebenar. */
    const [keadaan, setKeadaan] = useState('biasa');
    const [editHasratPelan, setEditHasratPelan] = useState(false);
    const [hasratPelan, setHasratPelan] = useState('Hari ini saya nak siapkan 30 video.');

    /* Otak sebenar — dimatikan sepenuhnya dalam mod pelan supaya halaman
       prototaip tidak membakar bacaan Firestore. */
    const nyata = useDashboard(user, { isPaid, enabled: modSebenar });
    const adminProdukBaru = bolehLihatProdukBaru(user);

    const baru = !modSebenar && keadaan === 'baru';
    const produk = keadaan === 'kosong' ? [] : PRODUK_BARU;

    /* Nilai yang dipapar oleh dua kad yang SUDAH bertukar rupa. */
    const ucapan = t(modSebenar ? nyata.greetingKey : 'dashboard.greeting.morning');
    const nama = modSebenar ? nyata.displayName : 'Zuheir';
    const teksHasrat = modSebenar ? nyata.wish : hasratPelan;
    const sedangEdit = modSebenar ? nyata.isEditingWish : editHasratPelan;
    const mulaEdit = () => (modSebenar ? nyata.startEditWish() : setEditHasratPelan(true));

    /* Seksyen lama yang dikongsi (DashboardSections) bergaya Tailwind
       `dark:` — ia perlu dibalut kelas `dark` HANYA semasa tema v3.9
       gelap. Balutan tetap `dark` di bawah tema terang memaksa palet
       gelapnya (teks amber pucat) ke atas halaman putih — cacat yang
       ditangkap user 2026-08-15. Dibaca semasa render sahaja: tema
       hanya boleh bertukar di halaman Settings, dan Dashboard
       di-mount semula setiap kali user kembali dari sana. */
    const kelasSeksyenLama = document.documentElement.classList.contains('s-light') ? '' : 'dark';

    const isi = (
        <>
            {/* 1 — Notis kata laluan. Dipapar hanya kepada akaun yang mendaftar
                   guna Google dan belum menetapkan kata laluan; tanpanya mereka
                   tidak boleh log masuk ke extension.
                   Mod sebenar memakai notis LAMA (belum direvamp). */}
            {modSebenar && nyata.needsPasswordSetup && (
                <div className={kelasSeksyenLama}>
                    {/* `t` dihantar masuk supaya seksyen lama ini ikut suis
                        bahasa halaman. Tanpa prop itu ia kekal Melayu — itulah
                        yang dimahukan pada rupa LAMA, bukan di sini. */}
                    <PasswordSetupNotice show t={t} />
                </div>
            )}

            <div className="sesaat-m-tight" style={{ padding: modSebenar ? '22px 28px 0' : '26px 28px 40px' }}>

                <PageHeader
                    title={t('dashboard.title')}
                    subtitle={t('dashboard.subtitle')}
                    actions={modSebenar ? undefined : (
                        <>
                            <Segmented
                                value={keadaan}
                                onChange={setKeadaan}
                                options={KEADAAN.map((k) => ({ value: k.value, label: t(k.labelKey) }))}
                            />
                            <span className="s-dim">{t('dashboard.planNote')}</span>
                        </>
                    )}
                />

                {/* Notis kata laluan — versi PELAN (rupa v3.9). */}
                {baru && (
                    <Card style={{ marginBottom: 16, borderColor: 'var(--s-warn-soft)' }}>
                        <CardBody style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                            <span style={{
                                width: 34, height: 34, borderRadius: 10, flex: 'none', display: 'grid', placeItems: 'center',
                                background: 'var(--s-warn-soft)', color: 'var(--s-warn)',
                            }}>
                                <KeyRound size={16} strokeWidth={1.9} />
                            </span>
                            <div style={{ flex: 1, minWidth: 220 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', marginBottom: 2 }}>
                                    {t('dashboard.passwordNotice.title')}
                                </div>
                                <p className="s-meta" style={{ fontSize: 13 }}>
                                    {t('dashboard.passwordNotice.desc')}
                                </p>
                            </div>
                            <Btn size="sm">{t('dashboard.passwordNotice.cta')}</Btn>
                        </CardBody>
                    </Card>
                )}

                {/* 2 — Ucapan + hasrat, dan group sokongan. Dua-dua sebaris. */}
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: 14, marginBottom: modSebenar ? 0 : 26,
                }}>
                    <Card>
                        <CardBody>
                            <p className="s-dim" style={{ marginBottom: 2 }}>{ucapan}</p>
                            <h2 className="s-title" style={{ fontSize: 22, marginBottom: 12 }}>{nama}</h2>

                            {/* Hasrat harian — teks yang pengguna sendiri tulis. Dalam mod
                                sebenar ia disimpan ke Firestore melalui useDashboard; dalam
                                mod pelan ia state tempatan semata-mata untuk menunjukkan
                                rupa mod edit. */}
                            {sedangEdit ? (
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <input
                                        autoFocus
                                        ref={modSebenar ? nyata.wishInputRef : undefined}
                                        value={modSebenar ? nyata.wishDraft : hasratPelan}
                                        maxLength={80}
                                        onChange={(e) => (modSebenar
                                            ? nyata.setWishDraft(e.target.value)
                                            : setHasratPelan(e.target.value))}
                                        onKeyDown={(e) => {
                                            if (modSebenar) { nyata.onWishKeyDown(e); return; }
                                            if (e.key === 'Enter') setEditHasratPelan(false);
                                        }}
                                        style={{
                                            flex: 1, minWidth: 180, background: 'var(--s-ground)', color: 'var(--s-ink)',
                                            border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r-sm)',
                                            padding: '7px 10px', fontSize: 13.5, fontFamily: 'inherit', outline: 'none',
                                        }}
                                    />
                                    <Btn
                                        size="sm"
                                        icon={<Check size={14} strokeWidth={2} />}
                                        disabled={modSebenar && nyata.savingWish}
                                        onClick={() => (modSebenar ? nyata.saveWish() : setEditHasratPelan(false))}
                                    >
                                        {modSebenar && nyata.savingWish ? t('common.saving') : t('common.save')}
                                    </Btn>
                                    {/* Batal — mod sebenar sahaja; rupa lama pun ada butang ini,
                                        jadi membuangnya bermakna pengguna hilang jalan keluar
                                        selepas tersilap taip. */}
                                    {modSebenar && (
                                        <Btn size="sm" variant="ghost" icon={<X size={14} strokeWidth={2} />}
                                            onClick={nyata.cancelEditWish}>
                                            {t('common.cancel')}
                                        </Btn>
                                    )}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <p className="s-meta" style={{ fontSize: 13.5 }}>{teksHasrat}</p>
                                    <button
                                        type="button"
                                        aria-label={t('dashboard.wish.editAria')}
                                        onClick={mulaEdit}
                                        style={{
                                            padding: 4, background: 'none', border: 0, cursor: 'pointer',
                                            color: 'var(--s-ink-3)', display: 'grid', placeItems: 'center',
                                        }}
                                    >
                                        <Pencil size={13} strokeWidth={1.9} />
                                    </button>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                <span style={{
                                    width: 34, height: 34, borderRadius: 10, flex: 'none', display: 'grid', placeItems: 'center',
                                    background: 'var(--s-ok-soft)', color: 'var(--s-ok)',
                                }}>
                                    <MessageCircle size={16} strokeWidth={1.9} />
                                </span>
                                <div style={{ minWidth: 0 }}>
                                    <h3 className="s-cardname" style={{ marginBottom: 3 }}>{t('dashboard.support.title')}</h3>
                                    <p className="s-meta" style={{ fontSize: 13 }}>
                                        {t('dashboard.support.desc')}
                                    </p>
                                </div>
                            </div>
                            <div style={{ marginTop: 'auto' }}>
                                {/* Link ejen yang merujuk pengguna, kalau ejen itu ada set;
                                    kalau tidak, group rasmi SESAAT ikut tier. */}
                                <Btn
                                    size="sm"
                                    icon={<ArrowRight size={14} strokeWidth={2} />}
                                    onClick={modSebenar
                                        ? () => window.open(nyata.supportLink, '_blank', 'noopener,noreferrer')
                                        : undefined}
                                >
                                    {t('dashboard.support.cta')}
                                </Btn>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                {/* ══ SEKSYEN DI BAWAH INI: MOD PELAN SAHAJA ══
                    Dalam mod sebenar, ketiga-tiganya dipapar dalam bentuk ASAL
                    (belum direvamp) di luar bekas ini — lihat hujung fail. */}
                {!modSebenar && (
                    <>
                        {/* Ringkasan pantas — TAMBAHAN, tiada dalam halaman asal.
                            Sebab dicadangkan: dashboard sekarang tidak memberitahu pengguna
                            keadaan akaunnya langsung (kuota tinggal berapa, storage penuh
                            ke belum). Dua angka itu yang paling kerap ditanya dalam group.
                            ⚠️ Ini cadangan — buang kalau kau tak mahu. */}
                        {!baru && (
                            <div style={{
                                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: 12, marginBottom: 26,
                            }}>
                                <StatTile label={t('dashboard.stat.quota')} value="34 / 100" hint={t('dashboard.stat.quotaHint')} />
                                <StatTile label={t('dashboard.stat.storage')} value="1.2 / 5 GB" hint={t('dashboard.stat.storageHint')} />
                                <StatTile label={t('dashboard.stat.videos')} value="126" hint={t('dashboard.stat.videosHint')} />
                            </div>
                        )}

                        {/* 3 — Produk Baru */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                            <div>
                                <p className="s-dim" style={{ marginBottom: 2 }}>{t('dashboard.newProducts.eyebrow')}</p>
                                <h2 className="s-title" style={{ fontSize: 16 }}>{t('dashboard.newProducts.title')}</h2>
                            </div>
                            <Btn size="sm" variant="ghost" icon={<ArrowRight size={14} strokeWidth={2} />}
                                onClick={() => onNavigate?.('/products-v3.9')}>
                                {t('dashboard.newProducts.seeAll')}
                            </Btn>
                        </div>

                        {produk.length === 0 ? (
                            <div style={{ marginBottom: 26 }}>
                                <EmptyState title={t('dashboard.newProducts.emptyTitle')}>
                                    {t('dashboard.newProducts.emptyDesc')}
                                </EmptyState>
                            </div>
                        ) : (
                            <div style={{
                                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
                                gap: 12, marginBottom: 26,
                            }}>
                                {produk.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => onNavigate?.('/products-v3.9')}
                                        className="s-card s-card--hover"
                                        style={{
                                            padding: 0, textAlign: 'left', cursor: 'pointer',
                                            fontFamily: 'inherit', overflow: 'hidden', border: '1px solid var(--s-line)',
                                        }}
                                    >
                                        <div style={{ aspectRatio: '1 / 1', background: p.warna, position: 'relative' }}>
                                            {p.ready && (
                                                <span style={{ position: 'absolute', top: 6, right: 6 }}>
                                                    <Badge tone="ok">READY</Badge>
                                                </span>
                                            )}
                                        </div>
                                        {/* Nama dipotong dua baris — nama produk TikTok kerap
                                            sangat panjang; tanpa had ini kad jadi tinggi tak sama. */}
                                        <div style={{
                                            padding: '8px 9px 10px', fontSize: 11.5, lineHeight: 1.35, color: 'var(--s-ink-2)',
                                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden', minHeight: 46,
                                        }}>
                                            {p.nama}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* 4 — Kalendar Kempen */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <CalendarDays size={16} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)' }} />
                            <h2 className="s-title" style={{ fontSize: 16 }}>{t('dashboard.campaign.title')}</h2>
                        </div>

                        <div className="s-list" style={{ marginBottom: 26 }}>
                            {KEMPEN.map((k) => (
                                <div key={k.nama} className="s-list__row">
                                    <span style={{
                                        width: 46, flex: 'none', textAlign: 'center',
                                        fontSize: 13, fontWeight: 600, color: 'var(--s-ink)',
                                    }}>{k.tarikh}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, color: 'var(--s-ink)' }}>{k.nama}</div>
                                        <p className="s-dim">
                                            {t(k.jenis === 'cuti' ? 'dashboard.campaign.holiday' : 'dashboard.campaign.platform')}
                                        </p>
                                    </div>
                                    {k.hari <= 7
                                        ? <Badge tone="warn">{t('dashboard.campaign.daysLeft', { n: k.hari })}</Badge>
                                        : <span className="s-dim">{t('dashboard.campaign.daysLeft', { n: k.hari })}</span>}
                                </div>
                            ))}
                        </div>

                        {/* 5 — Footer */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 12, flexWrap: 'wrap', paddingTop: 18, borderTop: '1px solid var(--s-line)',
                        }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                <span style={{
                                    width: 22, height: 22, borderRadius: 7, display: 'grid', placeItems: 'center',
                                    background: 'var(--s-brand-soft)', color: 'var(--s-brand)',
                                }}>
                                    <Zap size={13} strokeWidth={2} />
                                </span>
                                <span style={{ fontSize: 13, color: 'var(--s-ink-2)' }}>SESAAT</span>
                                <Badge tone="neutral">Beta</Badge>
                            </span>
                            <span className="s-dim">{t('dashboard.footer.rights')}</span>
                        </div>
                    </>
                )}
            </div>

            {/* ══ SEKSYEN BELUM DIREVAMP — MOD SEBENAR SAHAJA ══
                Bentuk, susunan dan fungsi dikekalkan bulat-bulat. Semasa tema
                gelap ia dibalut bekas `dark` supaya memakai palet gelapnya
                sendiri (tanpa itu kotak putih menyilaukan dalam panel gelap);
                semasa tema terang balutan itu digugurkan supaya ia memakai
                palet terang asalnya. Padding kiri/kanan datang daripada
                seksyen itu sendiri (px-4 sm:px-6), jadi ia sengaja duduk
                DI LUAR bekas berpadding di atas.

                `paddingBottom` — footer dahulu yang memberi ruang di kaki
                halaman. Selepas ia dibuang, seksyen terakhir melekat pada
                tepi bawah panel tanpa nilai ini. */}
            {modSebenar && (
                <div className={kelasSeksyenLama} style={{ paddingBottom: 28 }}>
                    {/* Produk Baru — ADMIN SAHAJA (arahan user 2026-08-21). Pagar
                        KEDUA; yang pertama dalam useDashboard.js melangkau bacaan
                        Firestore. Kalendar Kempen di bawah kekal untuk semua. */}
                    {adminProdukBaru && <NewProductsSection products={nyata.latestProducts} t={t} />}
                    <CampaignCalendarSection t={t} lang={lang} />
                </div>
            )}

            {/* 2026-10-29 (arahan user): popup "Selamat datang ke SESAAT!" (TutorialModal) dibuang. Video tutorial kekal di menu Tutorial (/tutorial) dan halaman Extension. */}
        </>
    );

    /* Halaman ini TIDAK memasang bingkainya sendiri (tukar 2026-08-11).
       Mod pelan dibalut oleh StudioShell; mod sebenar dibalut sekali di
       App.jsx (`BingkaiHalaman`) untuk seluruh app. Memasang di sini juga
       menghasilkan panel bersarang — kotak dalam kotak, jurang dua lapis. */
    return isi;
}
